import { SecondaryButton } from 'cdk-radial';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EVENT_NAME, Tabs } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import {
  useSubmitAuthorizedApCheck,
  useUpdateInvoiceState
} from 'hooks/useClientDataServices';
import { useErrorHandler } from 'hooks/useErrorHandler';
import { useFlagValue } from 'hooks/useFlags';
import { setSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { useDeclinePayment } from '../../hooks/useDeclinePayment';
import { messages } from '../../messages';
import { errors } from '../PrintChecks';
import {
  getSelectedVendorInvoicePairs,
  getIsCancelButtonDisabled
} from '../PrintChecks/slice';
import { setDueDate } from '../VendorsTable/slice';

export const CancelPaymentHeaderButton = (): React.JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const onNoChecksError = useErrorHandler(errors.noChecksSelectedToCancel);
  const isDisabled = useAppSelector(getIsCancelButtonDisabled);
  const selectedInvoices = useAppSelector(getSelectedVendorInvoicePairs);
  const onCancelSuccess = useCallback(() => {
    dispatch(
      setSuccessMessage(
        intl.formatMessage(
          selectedInvoices.length === 1
            ? messages.paymentCanceled
            : messages.paymentsCanceled
        )
      )
    );
    dispatch(setSelectedTab(Tabs.NeedsAuthorization));
    dispatch(setDueDate(''));
  }, [dispatch, intl, selectedInvoices.length]);
  const { mutate: updateInvoiceState, isLoading: isUpdateInvoiceStateLoading } =
    useUpdateInvoiceState();
  const { declinePayment, isLoading } = useDeclinePayment();
  const threeStepApprovalFlag = useFlagValue('dmsAccountingThreeStepApproval');
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');
  const amplitude = useAmplitude();

  const { mutate, isLoading: isUpdateIsAuthorizedLoading } =
    useSubmitAuthorizedApCheck(false);

  const handleCancelPaymentButton = useCallback(() => {
    if (!selectedInvoices.length) {
      onNoChecksError();
    } else if (threeStepApprovalFlag) {
      if (landingPageFlag === true) {
        amplitudeTracker(amplitude, EVENT_NAME.CancelPayment, {
          'Selection total': selectedInvoices.length
        });
        const vendorInvoicePairs = selectedInvoices.map(
          ({ vendorNo, invoiceId }) => ({
            vendorNo,
            invoiceId
          })
        );
        updateInvoiceState(
          {
            state: 'APPROVED',
            payload: { vendorInvoicePairs }
          },
          {
            onSuccess: onCancelSuccess
          }
        );
      } else {
        amplitudeTracker(amplitude, EVENT_NAME.CancelPayment, {
          'Selection total': selectedInvoices.length
        });
        mutate(selectedInvoices, {
          onSuccess: onCancelSuccess
        });
      }
    } else {
      declinePayment(selectedInvoices);
    }
  }, [
    amplitude,
    declinePayment,
    landingPageFlag,
    mutate,
    onCancelSuccess,
    onNoChecksError,
    selectedInvoices,
    threeStepApprovalFlag,
    updateInvoiceState
  ]);
  return (
    <SecondaryButton
      isDisabled={isDisabled}
      isLoading={
        isLoading || isUpdateIsAuthorizedLoading || isUpdateInvoiceStateLoading
      }
      dataTestId="cancel-payment-button"
      onClick={handleCancelPaymentButton}
      text={
        <FormattedMessage
          description="Cancel payment button label"
          defaultMessage="Cancel Payment"
        />
      }
    />
  );
};
